// 033 — Bloque I (indicadores de salud bucal) y utilidades de texto de la hoja 1,
// según la ficha SNS-MSP/HCU-form.033/2021 y su instructivo (bloque I, págs.
// 164-165 del PDF fuente). Todo es puro (sin React ni Medplum) para poder
// probarlo con reloj fijo y reusar el mismo cálculo en pantalla, PDF y FHIR.
//
// Regla del instructivo: la tabla tiene 6 filas fijas de piezas (2 permanentes
// + 1 temporal cada una). En cada fila se marca con X la pieza examinada
// (un diente por fila, "seis piezas en total"); si no hay ninguna presente,
// la fila se registra con una raya. Placa 0-3, cálculo 0-3 y gingivitis 0-1
// por pieza. En TOTALES: suma vertical dividida para el número de piezas
// examinadas; "cuando el resultado tiene decimales, no se aproxima al
// inmediato superior" (se trunca).
import type { Encounter, Observation, ObservationComponent, Reference } from '@medplum/fhirtypes';
import type { Par } from './index';

/** Filas fijas de la ficha: [permanente A, permanente B, temporal]. */
export const FILAS_INDICADORES: readonly (readonly [string, string, string])[] = [
  ['16', '17', '55'],
  ['11', '21', '51'],
  ['26', '27', '65'],
  ['36', '37', '75'],
  ['31', '41', '71'],
  ['46', '47', '85'],
];

export const OPCIONES_PLACA = ['0', '1', '2', '3'] as const;
export const OPCIONES_CALCULO = ['0', '1', '2', '3'] as const;
export const OPCIONES_GINGIVITIS = ['0', '1'] as const;

/** Valor de `pieza` cuando ninguna pieza de la fila está presente ("raya" del instructivo). */
export const PIEZA_RAYA = 'raya';

export interface FilaIndicadores {
  /** Código FDI de la pieza examinada (X de la ficha), `PIEZA_RAYA` (raya) o '' (sin registrar). */
  readonly pieza: string;
  readonly placa: string;
  readonly calculo: string;
  readonly gingivitis: string;
}

export const FILA_INDICADORES_VACIA: FilaIndicadores = { pieza: '', placa: '', calculo: '', gingivitis: '' };

export function filasIndicadoresIniciales(): FilaIndicadores[] {
  return FILAS_INDICADORES.map(() => ({ ...FILA_INDICADORES_VACIA }));
}

/** Piezas válidas para una fila (las 3 de la ficha). */
export function piezaEsDeLaFila(indice: number, pieza: string): boolean {
  return (FILAS_INDICADORES[indice] as readonly string[] | undefined)?.includes(pieza) === true;
}

export interface TotalIndicador {
  readonly suma: number;
  /** Cociente truncado (regla del instructivo: no se aproxima al inmediato superior). */
  readonly promedio: number;
  /** Cociente exacto, para el FHIR. */
  readonly promedioExacto: number;
}

export interface TotalesIndicadores {
  /** Filas con una pieza examinada. */
  readonly piezasExaminadas: number;
  /** Filas con pieza examinada pero con algún indicador sin registrar (no entran al promedio). */
  readonly filasIncompletas: number;
  readonly placa: TotalIndicador | null;
  readonly calculo: TotalIndicador | null;
  readonly gingivitis: TotalIndicador | null;
}

function esExaminada(fila: FilaIndicadores, indice: number): boolean {
  return piezaEsDeLaFila(indice, fila.pieza);
}

function total(valores: readonly number[]): TotalIndicador | null {
  if (valores.length === 0) return null;
  const suma = valores.reduce((a, b) => a + b, 0);
  return { suma, promedio: Math.floor(suma / valores.length), promedioExacto: suma / valores.length };
}

function numero(v: string): number | null {
  return /^\d$/.test(v) ? Number(v) : null;
}

export function totalesIndicadores(filas: readonly FilaIndicadores[]): TotalesIndicadores {
  const examinadas = filas.filter((f, i) => esExaminada(f, i));
  const completas = examinadas.filter((f) => numero(f.placa) !== null && numero(f.calculo) !== null && numero(f.gingivitis) !== null);
  // Solo se promedia sobre filas completas: una fila a medias no debe inflar ni bajar el promedio en silencio.
  const en = (campo: 'placa' | 'calculo' | 'gingivitis'): TotalIndicador | null => total(completas.map((f) => numero(f[campo]) as number));
  return { piezasExaminadas: examinadas.length, filasIncompletas: examinadas.length - completas.length, placa: en('placa'), calculo: en('calculo'), gingivitis: en('gingivitis') };
}

export type Traductor = (clave: string, opciones?: Record<string, unknown>) => string;

function textoTotal(t: Traductor, x: TotalIndicador | null, n: number): string {
  return x ? t('formularios.f033.indicadoresSaludBucal.totalValor', { promedio: x.promedio, suma: x.suma, n }) : '—';
}

/** `Par[]` del bloque I — una fila por cada una de las 6 de la ficha + la fila TOTALES. */
export function paresIndicadores(t: Traductor, filas: readonly FilaIndicadores[]): Par[] {
  const prefijo = t('formularios.f033.seccionIndicadoresSaludBucal');
  const pares: Par[] = FILAS_INDICADORES.map((piezas, i) => {
    const fila = filas[i] ?? FILA_INDICADORES_VACIA;
    let respuesta: string;
    if (fila.pieza === PIEZA_RAYA) respuesta = t('formularios.f033.indicadoresSaludBucal.sinPiezaPresente');
    else if (!esExaminada(fila, i)) respuesta = '—';
    else {
      respuesta = [
        `${t('formularios.f033.indicadoresSaludBucal.piezaExaminada')}: ${fila.pieza}`,
        `${t('formularios.f033.indicadoresSaludBucal.placa')}: ${fila.placa || '—'}`,
        `${t('formularios.f033.indicadoresSaludBucal.calculo')}: ${fila.calculo || '—'}`,
        `${t('formularios.f033.indicadoresSaludBucal.gingivitis')}: ${fila.gingivitis || '—'}`,
      ].join(' | ');
    }
    return { pregunta: `${prefijo} — ${piezas.join('/')}`, respuesta };
  });
  const tot = totalesIndicadores(filas);
  const n = tot.piezasExaminadas - tot.filasIncompletas;
  pares.push({
    pregunta: `${prefijo} — ${t('formularios.f033.indicadoresSaludBucal.totales')}`,
    respuesta: [
      `${t('formularios.f033.indicadoresSaludBucal.placa')}: ${textoTotal(t, tot.placa, n)}`,
      `${t('formularios.f033.indicadoresSaludBucal.calculo')}: ${textoTotal(t, tot.calculo, n)}`,
      `${t('formularios.f033.indicadoresSaludBucal.gingivitis')}: ${textoTotal(t, tot.gingivitis, n)}`,
      ...(tot.filasIncompletas > 0 ? [t('formularios.f033.indicadoresSaludBucal.filasIncompletas', { n: tot.filasIncompletas })] : []),
    ].join(' | '),
  });
  return pares;
}

export const NINGUNA = 'ninguna';

/** Enfermedad periodontal / oclusión / fluorosis: "en caso de no diagnosticar ninguna patología registrar una raya". */
export function textoOpcionIndicador(t: Traductor, prefijoOpciones: string, valor: string): string {
  if (!valor) return '—';
  if (valor === NINGUNA) return t('formularios.f033.indicadoresSaludBucal.ningunaRaya');
  return t(`${prefijoOpciones}.${valor}`);
}

// ---------------------------------------------------------------- hoja 1: textos

/** D y E: casilleros marcados + descripción; sin nada, "no refiere antecedentes" (instructivo D/E). */
export function parAntecedentes(
  t: Traductor,
  pregunta: string,
  prefijoI18n: string,
  claves: readonly string[],
  marcados: Readonly<Record<string, boolean>>,
  descripcion: string,
): Par {
  const lista = claves.filter((c) => marcados[c]).map((c) => t(`${prefijoI18n}.${c}`));
  const desc = descripcion.trim();
  if (lista.length === 0 && !desc) return { pregunta, respuesta: t('formularios.f033.noRefiereAntecedentes') };
  const partes = [lista.join(', ')].filter(Boolean);
  if (desc) partes.push(`${t('formularios.f033.descripcion')}: ${desc}`);
  return { pregunta, respuesta: partes.join(' — ') };
}

/** G: solo se imprimen los ítems con descripción; sin ninguno, "Sin patología aparente" (instructivo G). */
export function paresEstomatognatico(t: Traductor, items: readonly string[], valores: Readonly<Record<string, string>>): Par[] {
  const seccion = t('formularios.f033.seccionExamenEstomatognatico');
  const con = items.filter((i) => valores[i]?.trim());
  if (con.length === 0) return [{ pregunta: seccion, respuesta: t('formularios.f033.sinPatologiaAparente') }];
  return con.map((i) => ({ pregunta: `${seccion} — ${t(`formularios.f033.examenEstomatognatico.${i}`)}`, respuesta: (valores[i] ?? '').trim() }));
}

export const TIPOS_INFORME_EXAMENES = ['biometria', 'quimicaSanguinea', 'rayosX', 'otros'] as const;

/** M: tipos de examen con X (biometría, química sanguínea, rayos-X, otros) + texto del informe. */
export function parInformeExamenes(t: Traductor, tipos: Readonly<Record<string, boolean>>, informe: string): Par {
  const pregunta = t('formularios.f033.examenesComplementarios.informe');
  const lista = TIPOS_INFORME_EXAMENES.filter((k) => tipos[k]).map((k) => t(`formularios.f033.examenesComplementarios.tipos.${k}`));
  const texto = informe.trim();
  if (lista.length === 0 && !texto) return { pregunta, respuesta: '—' };
  return { pregunta, respuesta: [lista.join(', '), texto].filter(Boolean).join(' — ') };
}

// ---------------------------------------------------------------- FHIR

export const SISTEMA_INDICADORES_SALUD_BUCAL = 'http://logiacare.ec/fhir/indicadores-salud-bucal';

export interface IndicesCpoCeoParaFhir {
  readonly cpod: { readonly c: number; readonly p: number; readonly o: number; readonly total: number };
  readonly ceod: { readonly c: number; readonly e: number; readonly o: number; readonly total: number };
}

function comp(codigo: string, display: string, extra: Partial<ObservationComponent>): ObservationComponent {
  return { code: { coding: [{ system: SISTEMA_INDICADORES_SALUD_BUCAL, code: codigo, display }] }, ...extra };
}

/**
 * `Observation` de indicadores de salud bucal + CPO-ceo. Un component por dato con valor
 * (por pieza: placa/cálculo/gingivitis; promedios; índices; periodontal/oclusión/fluorosis).
 * `undefined` si no hay nada capturado. `ahora` es inyectable para reloj fijo en pruebas.
 */
export function construirObservationIndicadores(
  args: {
    filas: readonly FilaIndicadores[];
    periodontal: string;
    oclusion: string;
    fluorosis: string;
    indices: IndicesCpoCeoParaFhir;
    hayOdontograma: boolean;
    /** Claves de `TIPOS_INFORME_EXAMENES` marcadas (M. Informe de exámenes). */
    tiposInforme: readonly string[];
  },
  subject: Observation['subject'],
  encounter: Reference<Encounter> | undefined,
  ahora: string,
): Observation | undefined {
  const componentes: ObservationComponent[] = [];
  args.filas.forEach((f, i) => {
    // Toda fila con dato deja rastro: la pieza examinada o la raya (ninguna presente).
    if (f.pieza === PIEZA_RAYA || esExaminada(f, i)) componentes.push(comp(`fila-${FILAS_INDICADORES[i][0]}`, `fila ${FILAS_INDICADORES[i].join('/')}`, { valueString: f.pieza }));
    if (!esExaminada(f, i)) return;
    for (const campo of ['placa', 'calculo', 'gingivitis'] as const) {
      const v = numero(f[campo]);
      if (v !== null) componentes.push(comp(`${campo}-${f.pieza}`, `${campo} pieza ${f.pieza}`, { valueInteger: v }));
    }
  });
  const tot = totalesIndicadores(args.filas);
  for (const campo of ['placa', 'calculo', 'gingivitis'] as const) {
    const x = tot[campo];
    if (x) {
      // `promedio-*` = el valor oficial que se imprime (truncado); el decimal exacto va aparte.
      componentes.push(comp(`promedio-${campo}`, `promedio ${campo}`, { valueInteger: x.promedio }));
      componentes.push(comp(`promedio-exacto-${campo}`, `promedio exacto ${campo}`, { valueQuantity: { value: Number(x.promedioExacto.toFixed(2)) } }));
    }
  }
  for (const [codigo, valor] of [
    ['enfermedad-periodontal', args.periodontal],
    ['tipo-oclusion', args.oclusion],
    ['nivel-fluorosis', args.fluorosis],
  ] as const) {
    if (valor) componentes.push(comp(codigo, codigo, { valueString: valor }));
  }
  if (args.tiposInforme.length > 0) componentes.push(comp('tipos-informe-examenes', 'tipos de informe de exámenes', { valueString: args.tiposInforme.join(', ') }));
  if (args.hayOdontograma) {
    const { cpod, ceod } = args.indices;
    componentes.push(
      comp('cpod-cariado', 'CPOD C', { valueInteger: cpod.c }),
      comp('cpod-perdido', 'CPOD P', { valueInteger: cpod.p }),
      comp('cpod-obturado', 'CPOD O', { valueInteger: cpod.o }),
      comp('cpod-total', 'CPOD total', { valueInteger: cpod.total }),
      comp('ceod-cariado', 'ceod c', { valueInteger: ceod.c }),
      comp('ceod-extraccion', 'ceod e', { valueInteger: ceod.e }),
      comp('ceod-obturado', 'ceod o', { valueInteger: ceod.o }),
      comp('ceod-total', 'ceod total', { valueInteger: ceod.total }),
    );
  }
  if (componentes.length === 0) return undefined;
  return { resourceType: 'Observation', status: 'final', code: { text: 'Indicadores de salud bucal' }, subject, encounter, effectiveDateTime: ahora, component: componentes };
}
